/**
 * Auth Service client for frontend communication with the Auth Service.
 */

import config from '../config';
import { createTrackedHeaders, CALL_TYPES } from '../utils/apiCallTracker';

class AuthService {
  constructor() {
    this.baseUrl = config.api.authServiceUrl;
  }

  /**
   * Creates headers with proper authentication and call type tracking
   * @param {string} token - Auth token (optional)
   * @param {string} callType - Type of call (manual/automatic)
   * @param {Object} additionalHeaders - Additional headers
   * @returns {Object} Headers object
   */
  _createHeaders(token = null, callType = CALL_TYPES.AUTOMATIC, additionalHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...createTrackedHeaders(callType),
      ...additionalHeaders
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Login with username and password
   * @param {string} username 
   * @param {string} password 
   * @returns {Promise<{access_token: string, token_type: string}>}
   */
  async login(username, password) {
    // Safest: use URLSearchParams, convert to string, only required fields
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('grant_type', 'password');

    const response = await fetch(`${this.baseUrl}/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.MANUAL) // Login is always manual
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(error.detail || 'Login failed');
    }

    return await response.json();
  }

  /**
   * Logout (invalidate token)
   * @param {string} token 
   */
  async logout(token) {
    try {
      await fetch(`${this.baseUrl}/auth/logout`, {
        method: 'POST',
        headers: this._createHeaders(token, CALL_TYPES.MANUAL), // Logout is manual
      });
    } catch (error) {
      console.warn('Logout request failed:', error);
      // Continue with local logout even if server request fails
    }
  }

  /**
   * Get current user information
   * @param {string} token 
   * @param {boolean} isManual - Whether this is a manual request (default: automatic)
   * @returns {Promise<Object>}
   */
  async getCurrentUser(token, isManual = false) {
    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;
    
    const response = await fetch(`${this.baseUrl}/auth/me`, {
      method: 'GET',
      headers: this._createHeaders(token, callType),
    });

    if (!response.ok) {
      throw new Error('Failed to get user information');
    }

    return await response.json();
  }

  /**
   * Get current user permissions
   * @param {string} token 
   * @param {boolean} isManual - Whether this is a manual request (default: automatic)
   * @returns {Promise<{permissions: string[]}>}
   */
  async getUserPermissions(token, isManual = false) {
    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;
    
    const response = await fetch(`${this.baseUrl}/auth/me/permissions`, {
      method: 'GET',
      headers: this._createHeaders(token, callType),
    });

    if (!response.ok) {
      throw new Error('Failed to get user permissions');
    }

    return await response.json();
  }

  /**
   * Refresh token (extend expiry)
   * @param {string} token 
   * @returns {Promise<Object>}
   */
  async refreshToken(token) {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${this.baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.AUTOMATIC), // Token refresh is automatic
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    return await response.json();
  }

  /**
   * List all users (admin only)
   * @param {string} token 
   * @param {boolean} isManual - Whether this is a manual request (default: automatic for page loads)
   * @returns {Promise<{users: Object[]}>}
   */
  async listUsers(token, isManual = false) {
    return this._listUsers(token, isManual, '/auth/users');
  }

  async listTradeTrackerUsers(token, isManual = false) {
    return this._listUsers(token, isManual, '/auth/users/directory');
  }

  async _listUsers(token, isManual, path) {
    console.log('AuthService.listUsers called with baseUrl:', this.baseUrl);
    
    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: 'GET',
        headers: this._createHeaders(token, callType),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      console.log('Response received:', response.status, response.statusText);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to list users' }));
        throw new Error(error.detail || 'Failed to list users');
      }

      const data = await response.json();
      console.log('Data received:', data);
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out after 30 seconds');
      }
      throw error;
    }
  }

  /**
   * Create a new user (admin only)
   * @param {string} token 
   * @param {Object} userData 
   * @returns {Promise<Object>}
   */
  async createUser(token, userData) {
    const response = await fetch(`${this.baseUrl}/auth/users`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL), // Creating user is always manual
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to create user' }));
      throw new Error(error.detail || 'Failed to create user');
    }

    return await response.json();
  }

  /**
   * Update a user (admin only)
   * @param {string} token 
   * @param {string} username 
   * @param {Object} userData 
   * @returns {Promise<Object>}
   */
  async updateUser(token, username, userData) {
    const response = await fetch(`${this.baseUrl}/auth/users/${username}`, {
      method: 'PUT',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL), // Updating user is always manual
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to update user' }));
      throw new Error(error.detail || 'Failed to update user');
    }

    return await response.json();
  }

  /**
   * Update current user's profile
   * @param {string} token 
   * @param {Object} profileData 
   * @returns {Promise<Object>}
   */
  async updateProfile(token, profileData) {
    const response = await fetch(`${this.baseUrl}/auth/me/profile`, {
      method: 'PUT',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL), // Profile update is always manual
      body: JSON.stringify(profileData)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to update profile' }));
      throw new Error(error.detail || 'Failed to update profile');
    }

    return await response.json();
  }

  /**
   * Delete a user (admin only)
   * @param {string} token 
   * @param {string} username 
   * @returns {Promise<Object>}
   */
  async deleteUser(token, username) {
    const response = await fetch(`${this.baseUrl}/auth/users/${username}`, {
      method: 'DELETE',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL), // Delete is always manual
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to delete user' }));
      throw new Error(error.detail || 'Failed to delete user');
    }

    return await response.json();
  }

  /**
   * Reset user password (admin only)
   * @param {string} token 
   * @param {string} username 
   * @param {string} newPassword 
   * @returns {Promise<Object>}
   */
  async resetUserPassword(token, username, newPassword) {
    const response = await fetch(`${this.baseUrl}/auth/users/${username}/reset-password`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL), // Password reset is always manual
      body: JSON.stringify({ new_password: newPassword })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to reset password' }));
      throw new Error(error.detail || 'Failed to reset password');
    }

    return await response.json();
  }

  /**
   * Check service health
   * @returns {Promise<Object>}
   */
  async checkHealth() {
    const response = await fetch(`${this.baseUrl}/auth/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.AUTOMATIC) // Health check is automatic
      },
    });

    if (!response.ok) {
      throw new Error('Auth service is not healthy');
    }

    return await response.json();
  }

  /**
   * Record a navigation event
   * @param {string} token 
   * @param {Object} navigationData 
   * @returns {Promise<Object>}
   */
  async recordNavigation(token, navigationData) {
    try {
      const response = await fetch(`${this.baseUrl}/auth/activity/navigation`, {
        method: 'POST',
        headers: this._createHeaders(token, CALL_TYPES.AUTOMATIC), // Navigation tracking is automatic
        body: JSON.stringify(navigationData)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to record navigation' }));
        console.warn('Navigation tracking failed:', error.detail || 'Failed to record navigation');
        return; // Don't throw error for navigation tracking to avoid disrupting user experience
      }

      return await response.json();
    } catch (error) {
      console.warn('Navigation tracking request failed:', error);
      // Don't throw error for navigation tracking to avoid disrupting user experience
    }
  }

  /**
   * Get user activity history
   * @param {string} token 
   * @param {string} username 
   * @param {number} limit 
   * @param {number} offset 
   * @param {boolean} isManual - Whether this is a manual request (default: automatic for page loads)
   * @returns {Promise<Object>}
   */
  async getUserActivity(token, username, limit = 100, offset = 0, isManual = false) {
    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;
    
    const response = await fetch(`${this.baseUrl}/auth/activity/user/${username}?limit=${limit}&offset=${offset}`, {
      method: 'GET',
      headers: this._createHeaders(token, callType),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to get user activity' }));
      throw new Error(error.detail || 'Failed to get user activity');
    }

    return await response.json();
  }

  /**
   * Search activities with filters
   * @param {string} token 
   * @param {Object} filterParams 
   * @param {boolean} isManual - Whether this is a manual request (default: manual for search)
   * @returns {Promise<Object>}
   */
  async searchActivities(token, filterParams, isManual = true) {
    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;
    
    const response = await fetch(`${this.baseUrl}/auth/activity/search`, {
      method: 'POST',
      headers: this._createHeaders(token, callType),
      body: JSON.stringify(filterParams)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to search activities' }));
      throw new Error(error.detail || 'Failed to search activities');
    }

    return await response.json();
  }

  /**
   * Get user activity analytics
   * @param {string} token 
   * @param {string} username 
   * @param {string} startDate 
   * @param {string} endDate 
   * @param {boolean} isManual - Whether this is a manual request (default: automatic for page loads)
   * @returns {Promise<Object>}
   */
  async getUserAnalytics(token, username, startDate = null, endDate = null, isManual = false) {
    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;
    
    let url = `${this.baseUrl}/auth/activity/analytics/${username}`;
    const params = new URLSearchParams();
    
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: this._createHeaders(token, callType),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to get user analytics' }));
      throw new Error(error.detail || 'Failed to get user analytics');
    }

    return await response.json();
  }

  /**
   * Get current user's activity analytics
   * @param {string} token 
   * @param {string} startDate 
   * @param {string} endDate 
   * @param {boolean} isManual - Whether this is a manual request (default: automatic for page loads)
   * @returns {Promise<Object>}
   */
  async getCurrentUserAnalytics(token, startDate = null, endDate = null, isManual = false) {
    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;
    
    let url = `${this.baseUrl}/auth/activity/analytics`;
    const params = new URLSearchParams();
    
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: this._createHeaders(token, callType),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to get analytics' }));
      throw new Error(error.detail || 'Failed to get analytics');
    }

    return await response.json();
  }

  /**
   * Get list of usernames for dropdown filtering
   * @param {string} token 
   * @param {boolean} isManual - Whether this is a manual request (default: automatic for page loads)
   * @returns {Promise<Object>} Response with usernames array
   */
  async getUsernames(token, isManual = false) {
    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;
    
    const response = await fetch(`${this.baseUrl}/auth/users/usernames`, {
      method: 'GET',
      headers: this._createHeaders(token, callType),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to get usernames' }));
      throw new Error(error.detail || 'Failed to get usernames');
    }

    return await response.json();
  }

  /**
   * Get multi-user analytics
   * @param {string} token 
   * @param {Array<string>} usernames - Array of usernames to get analytics for
   * @param {string} startDate - Start date (ISO format)
   * @param {string} endDate - End date (ISO format)
   * @param {boolean} isManual - Whether this is a manual request
   * @returns {Promise<Object>} Combined analytics data
   */
  async getMultiUserAnalytics(token, usernames, startDate = null, endDate = null, isManual = false) {
    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;
    
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    
    const response = await fetch(`${this.baseUrl}/auth/activity/analytics/multi${queryString}`, {
      method: 'POST',
      headers: this._createHeaders(token, callType),
      body: JSON.stringify(usernames)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to get multi-user analytics' }));
      throw new Error(error.detail || 'Failed to get multi-user analytics');
    }

    return await response.json();
  }

  // =============================================================================
  // MFA (Multi-Factor Authentication) Methods
  // =============================================================================

  /**
   * Verify MFA code and complete login
   * @param {string} sessionToken - Temporary session token from initial login
   * @param {string} code - The 6-digit MFA verification code
   * @returns {Promise<{access_token: string, token_type: string, mfa_verified: boolean}>}
   */
  async verifyMfa(sessionToken, code) {
    const response = await fetch(`${this.baseUrl}/auth/mfa/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.MANUAL)
      },
      body: JSON.stringify({
        session_token: sessionToken,
        code: code
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'MFA verification failed' }));
      throw new Error(error.detail || 'MFA verification failed');
    }

    return await response.json();
  }

  /**
   * Get current user's MFA status
   * @param {string} token - Auth token
   * @param {boolean} isManual - Whether this is a manual request
   * @returns {Promise<{mfa_enabled: boolean, mfa_method: string|null}>}
   */
  async getMfaStatus(token, isManual = false) {
    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;
    
    const response = await fetch(`${this.baseUrl}/auth/mfa/status`, {
      method: 'GET',
      headers: this._createHeaders(token, callType)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to get MFA status' }));
      throw new Error(error.detail || 'Failed to get MFA status');
    }

    return await response.json();
  }

  /**
   * Initialize MFA setup
   * @param {string} token - Auth token
   * @param {string} method - MFA method: 'totp'
   * @returns {Promise<{method: string, secret: string|null, qr_code_uri: string|null, message: string}>}
   */
  async setupMfa(token, method) {
    const body = { method };

    const response = await fetch(`${this.baseUrl}/auth/mfa/setup`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to setup MFA' }));
      throw new Error(error.detail || 'Failed to setup MFA');
    }

    return await response.json();
  }

  /**
   * Enable MFA by verifying setup code
   * @param {string} token - Auth token
   * @param {string} code - The 6-digit verification code
   * @returns {Promise<{message: string, mfa_method: string}>}
   */
  async enableMfa(token, code) {
    const response = await fetch(`${this.baseUrl}/auth/mfa/enable`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL),
      body: JSON.stringify({ code })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to enable MFA' }));
      throw new Error(error.detail || 'Failed to enable MFA');
    }

    return await response.json();
  }

  /**
   * Disable MFA
   * @param {string} token - Auth token
   * @param {string} password - Current password for verification
   * @param {string|null} code - Optional MFA code for additional verification
   * @returns {Promise<{message: string}>}
   */
  async disableMfa(token, password, code = null) {
    const body = { password };
    if (code) {
      body.code = code;
    }

    const response = await fetch(`${this.baseUrl}/auth/mfa/disable`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to disable MFA' }));
      throw new Error(error.detail || 'Failed to disable MFA');
    }

    return await response.json();
  }

  /**
   * Cancel pending MFA setup
   * @param {string} token - Auth token
   * @returns {Promise<{message: string}>}
   */
  async cancelMfaSetup(token) {
    const response = await fetch(`${this.baseUrl}/auth/mfa/setup/cancel`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to cancel MFA setup' }));
      throw new Error(error.detail || 'Failed to cancel MFA setup');
    }

    return await response.json();
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
